import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Map as MapLibreMap,
  Source,
  Layer,
  NavigationControl,
  ScaleControl,
  type MapLayerMouseEvent,
  type MapRef,
} from '@vis.gl/react-maplibre';
import type { GeoJSONSource, FilterSpecification } from 'maplibre-gl';
import type { FeatureCollection, Point, MultiPolygon, Polygon, Feature } from 'geojson';
import type { Landmark } from '../types/landmark';
import type { Business } from '../types/business';
import type { AntwerpLandmark } from '../types/antwerp-landmark';
import { useLandmarkStore } from '../state/landmarks';
import { BaselinePopup } from './BaselinePopup';
import { PaperFrameOverlay } from './PaperFrameOverlay';
import { HISTORICAL_OVERLAYS, tileUrlForOverlay } from '../lib/historical-overlays';
import { styleForBase } from '../lib/base-layers';

// Center on Flanders — roughly the centroid of the region. Default zoom 8
// fits the whole region in a typical viewport; min zoom 7 lets users zoom
// out a touch for orientation; max zoom 19 supports street-level detail.
const FLANDERS_CENTER: [number, number] = [4.5, 51.05];
const DEFAULT_ZOOM = 8;
const MIN_ZOOM = 7;
const MAX_ZOOM = 19;
// At ~80k markers, lift the cluster ceiling so streets stay legible at high
// zoom but mid-zoom levels stay clean. Clusters break apart at zoom 16.
const CLUSTER_MAX_ZOOM = 16;
const CLUSTER_RADIUS = 60;
const DEFAULT_FLY_TO_ZOOM = 17;

interface HeritageMapProps {
  landmarks: Landmark[];
  businesses: Business[];
  antwerpLandmarks: AntwerpLandmark[];
  zone: Feature<MultiPolygon | Polygon, { id: number; name: string }>;
}

export function HeritageMap({ landmarks, businesses, antwerpLandmarks, zone }: HeritageMapProps) {
  const selectLandmark = useLandmarkStore((state) => state.selectLandmark);
  const selectBusiness = useLandmarkStore((state) => state.selectBusiness);
  const selectAntwerpLandmark = useLandmarkStore((state) => state.selectAntwerpLandmark);
  const clearSelection = useLandmarkStore((state) => state.clearSelection);
  const flyToTarget = useLandmarkStore((state) => state.flyToTarget);
  const flyToZoom = useLandmarkStore((state) => state.flyToZoom);
  const consumeFlyToTarget = useLandmarkStore((state) => state.consumeFlyToTarget);
  const overlays = useLandmarkStore((state) => state.overlays);
  const baseLayer = useLandmarkStore((state) => state.baseLayer);
  const heritageVisible = useLandmarkStore((state) => state.heritageVisible);
  const antwerpHistoricalVisible = useLandmarkStore((state) => state.antwerpHistoricalVisible);
  const antwerpHiddenCategories = useLandmarkStore((state) => state.antwerpHiddenCategories);
  const artistSelection = useLandmarkStore((state) => state.artistSelection);
  const artistSelectionOnly = useLandmarkStore((state) => state.artistSelectionOnly);
  const openContextMenu = useLandmarkStore((state) => state.openContextMenu);
  const mapRef = useRef<MapRef>(null);
  const antwerpLayerVisible = antwerpHistoricalVisible;

  // Maplibre filter: category hide-list and (optionally) restrict to the
  // artist-selection set. Combined with `all` when both are active.
  const antwerpCategoryFilter = useMemo<FilterSpecification | null>(() => {
    const filters: unknown[] = [];
    if (antwerpHiddenCategories.size > 0) {
      const hidden = [...antwerpHiddenCategories];
      filters.push(['!', ['in', ['get', 'category'], ['literal', hidden]]]);
    }
    if (artistSelectionOnly) {
      const ids = [...artistSelection];
      filters.push(['in', ['get', 'antwerpLandmarkId'], ['literal', ids]]);
    }
    if (filters.length === 0) return null;
    if (filters.length === 1) return filters[0] as FilterSpecification;
    return ['all', ...filters] as unknown as FilterSpecification;
  }, [antwerpHiddenCategories, artistSelectionOnly, artistSelection]);

  const featureCollection = useMemo<FeatureCollection<Point>>(
    () => ({
      type: 'FeatureCollection',
      features: landmarks.map((landmark) => ({
        type: 'Feature',
        id: landmark.id,
        geometry: { type: 'Point', coordinates: landmark.coordinates },
        properties: {
          landmarkId: landmark.id,
          name: landmark.name,
          tier: landmark.tier,
        },
      })),
    }),
    [landmarks],
  );

  const businessCollection = useMemo<FeatureCollection<Point>>(
    () => ({
      type: 'FeatureCollection',
      features: businesses.map((business) => ({
        type: 'Feature',
        id: business.id,
        geometry: { type: 'Point', coordinates: business.coordinates },
        properties: { businessId: business.id, name: business.name, category: business.category },
      })),
    }),
    [businesses],
  );

  const antwerpLandmarkCollection = useMemo<FeatureCollection<Point>>(
    () => ({
      type: 'FeatureCollection',
      features: antwerpLandmarks.map((al) => ({
        type: 'Feature',
        id: al.id,
        geometry: { type: 'Point', coordinates: al.coordinates },
        properties: {
          antwerpLandmarkId: al.id,
          name: al.name,
          category: al.category,
          period: al.period,
          stillStands: al.stillStands,
        },
      })),
    }),
    [antwerpLandmarks],
  );

  const antwerpLandmarksById = useMemo<Record<string, AntwerpLandmark>>(() => {
    const result: Record<string, AntwerpLandmark> = {};
    for (const al of antwerpLandmarks) result[al.id] = al;
    return result;
  }, [antwerpLandmarks]);

  const zoneCollection = useMemo<FeatureCollection<MultiPolygon | Polygon>>(
    () => ({ type: 'FeatureCollection', features: [zone] }),
    [zone],
  );

  const landmarksById = useMemo<Record<string, Landmark>>(() => {
    const result: Record<string, Landmark> = {};
    for (const landmark of landmarks) result[landmark.id] = landmark;
    return result;
  }, [landmarks]);

  const businessesById = useMemo<Record<string, Business>>(() => {
    const result: Record<string, Business> = {};
    for (const business of businesses) result[business.id] = business;
    return result;
  }, [businesses]);

  useEffect(() => {
    if (flyToTarget === null) return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    map.flyTo({
      center: flyToTarget,
      zoom: flyToZoom ?? DEFAULT_FLY_TO_ZOOM,
      essential: true,
    });
    consumeFlyToTarget();
  }, [flyToTarget, flyToZoom, consumeFlyToTarget]);

  // Right-click anywhere on the map → open the "Add POI here" context menu.
  const handleContextMenu = useCallback(
    (event: MapLayerMouseEvent) => {
      // event.originalEvent is the native MouseEvent; preventing it stops the
      // browser's built-in context menu.
      event.originalEvent.preventDefault();
      openContextMenu({
        lng: event.lngLat.lng,
        lat: event.lngLat.lat,
        screenX: event.point.x,
        screenY: event.point.y,
      });
    },
    [openContextMenu],
  );

  const handleClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature) {
        clearSelection();
        return;
      }

      if (feature.properties?.cluster) {
        const clusterId = feature.properties.cluster_id as number;
        const source = event.target.getSource('landmarks') as GeoJSONSource | undefined;
        if (!source) return;
        void source.getClusterExpansionZoom(clusterId).then((zoom) => {
          if (feature.geometry.type !== 'Point') return;
          event.target.easeTo({
            center: feature.geometry.coordinates as [number, number],
            zoom,
            duration: 500,
          });
        });
        return;
      }

      const businessId = feature.properties?.businessId as string | undefined;
      if (businessId) {
        const business = businessesById[businessId];
        if (business) selectBusiness(business);
        return;
      }

      const antwerpLandmarkId = feature.properties?.antwerpLandmarkId as string | undefined;
      if (antwerpLandmarkId) {
        const al = antwerpLandmarksById[antwerpLandmarkId];
        if (al) selectAntwerpLandmark(al);
        return;
      }

      const landmarkId = feature.properties?.landmarkId as string | undefined;
      if (!landmarkId) return;
      const landmark = landmarksById[landmarkId];
      if (landmark) selectLandmark(landmark);
    },
    [landmarksById, businessesById, antwerpLandmarksById, selectLandmark, selectBusiness, selectAntwerpLandmark, clearSelection],
  );

  return (
    <div data-testid="map-container" style={{ position: 'absolute', inset: 0 }}>
      <MapLibreMap
        ref={mapRef}
        initialViewState={{
          longitude: FLANDERS_CENTER[0],
          latitude: FLANDERS_CENTER[1],
          zoom: DEFAULT_ZOOM,
        }}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        mapStyle={styleForBase(baseLayer)}
        interactiveLayerIds={[
          'clusters',
          'unclustered-featured',
          'unclustered-baseline',
          'business-marker',
          'antwerp-historical-marker',
        ]}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        attributionControl={false}
        cursor="auto"
      >
        <NavigationControl position="top-right" showCompass={false} />
        <ScaleControl position="bottom-left" maxWidth={120} unit="metric" />

        {/*
          Historical raster overlays — drawn ABOVE the modern base style and
          BELOW the zone polygon + markers. Order in the JSX = order in the
          MapLibre layer stack. Hidden layers still mount but with opacity 0
          so toggling on/off is a paint change, not a source-add cycle.
        */}
        {HISTORICAL_OVERLAYS.map((overlay) => {
          const state = overlays[overlay.id];
          return (
            <Source
              key={overlay.id}
              id={`historical-${overlay.id}`}
              type="raster"
              tiles={[tileUrlForOverlay(overlay.layerName)]}
              tileSize={256}
              minzoom={0}
              maxzoom={21}
            >
              <Layer
                id={`historical-${overlay.id}-layer`}
                type="raster"
                paint={{
                  'raster-opacity': state.visible ? state.opacity : 0,
                  'raster-opacity-transition': { duration: 200, delay: 0 },
                }}
              />
            </Source>
          );
        })}

        {/*
          Topaz-upscaled Ferraris tiles for 6 city centers at z=16+. Sits on
          top of the Ferraris layer so when user zooms past Geopunt's native
          z=15, these higher-res tiles take over at the 6 city extents.
          Outside those extents the request 404s and MapLibre keeps Geopunt's
          bilinear-upscaled layer underneath.
        */}
        {overlays.ferraris ? (
          <Source
            id="ferraris-cities-upscaled"
            type="raster"
            tiles={['/ferraris-cities/{z}/{x}/{y}.png']}
            tileSize={256}
            minzoom={16}
            maxzoom={20}
          >
            <Layer
              id="ferraris-cities-upscaled-layer"
              type="raster"
              paint={{
                'raster-opacity': overlays.ferraris.visible ? overlays.ferraris.opacity : 0,
                'raster-opacity-transition': { duration: 200, delay: 0 },
              }}
            />
          </Source>
        ) : null}

        <Source id="zone" type="geojson" data={zoneCollection}>
          <Layer
            id="zone-fill"
            type="fill"
            paint={{ 'fill-color': '#c9a55c', 'fill-opacity': 0.05 }}
          />
          <Layer
            id="zone-outline"
            type="line"
            paint={{
              'line-color': '#8b3a2a',
              'line-width': 1.5,
              'line-opacity': 0.5,
              'line-dasharray': [3, 2],
            }}
          />
        </Source>

        <Source
          id="landmarks"
          type="geojson"
          data={featureCollection}
          cluster
          clusterMaxZoom={CLUSTER_MAX_ZOOM}
          clusterRadius={CLUSTER_RADIUS}
        >
          <Layer
            id="clusters"
            type="circle"
            filter={['has', 'point_count']}
            layout={{ visibility: heritageVisible ? 'visible' : 'none' }}
            paint={{
              'circle-color': '#8b3a2a',
              'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 30, 24, 100, 30],
              'circle-stroke-color': '#fffdf2',
              'circle-stroke-width': 2,
              'circle-opacity': 0.85,
            }}
          />
          <Layer
            id="cluster-count"
            type="symbol"
            filter={['has', 'point_count']}
            layout={{
              'text-field': ['get', 'point_count_abbreviated'],
              'text-font': ['Noto Sans Regular'],
              'text-size': 13,
              'text-allow-overlap': true,
              visibility: heritageVisible ? 'visible' : 'none',
            }}
            paint={{ 'text-color': '#fffdf2' }}
          />
          <Layer
            id="unclustered-featured-halo"
            type="circle"
            filter={['all', ['!', ['has', 'point_count']], ['==', ['get', 'tier'], 'featured']]}
            layout={{ visibility: heritageVisible ? 'visible' : 'none' }}
            paint={{
              'circle-color': '#c9a55c',
              'circle-radius': 14,
              'circle-opacity': 0.35,
              'circle-blur': 0.5,
            }}
          />
          <Layer
            id="unclustered-featured"
            type="circle"
            filter={['all', ['!', ['has', 'point_count']], ['==', ['get', 'tier'], 'featured']]}
            layout={{ visibility: heritageVisible ? 'visible' : 'none' }}
            paint={{
              'circle-color': '#8b3a2a',
              'circle-radius': 8,
              'circle-stroke-color': '#fffdf2',
              'circle-stroke-width': 2,
              'circle-opacity': 0.95,
            }}
          />
          <Layer
            id="unclustered-baseline"
            type="circle"
            filter={['all', ['!', ['has', 'point_count']], ['==', ['get', 'tier'], 'baseline']]}
            layout={{ visibility: heritageVisible ? 'visible' : 'none' }}
            paint={{
              'circle-color': '#6b5d44',
              'circle-radius': 4,
              'circle-stroke-color': '#fffdf2',
              'circle-stroke-width': 1,
              'circle-opacity': 0.65,
            }}
          />
        </Source>

        <Source id="antwerp-historical" type="geojson" data={antwerpLandmarkCollection}>
          <Layer
            id="antwerp-historical-glow"
            type="circle"
            {...(antwerpCategoryFilter ? { filter: antwerpCategoryFilter } : {})}
            layout={{ visibility: antwerpLayerVisible ? 'visible' : 'none' }}
            paint={{
              'circle-color': '#c9a55c',
              'circle-radius': 16,
              'circle-opacity': 0.35,
              'circle-blur': 0.55,
            }}
          />
          <Layer
            id="antwerp-historical-marker"
            type="circle"
            {...(antwerpCategoryFilter ? { filter: antwerpCategoryFilter } : {})}
            layout={{ visibility: antwerpLayerVisible ? 'visible' : 'none' }}
            paint={{
              'circle-color': [
                'match',
                ['get', 'category'],
                'folklore', '#a020f0',
                'fortification', '#5d4037',
                'church', '#8b3a2a',
                'abbey', '#5d2e8c',
                'castle', '#7c4a1e',
                'market', '#388e3c',
                'palace', '#1976d2',
                'house', '#0288d1',
                'guild', '#6d4c41',
                'almshouse', '#7b1fa2',
                'demolished', '#9e9e9e',
                'red-light', '#c2185b',
                'tavern', '#e65100',
                'painter', '#f9a825',
                'monument', '#455a64',
                'scene', '#00897b',
                'character', '#d81b60',
                'object', '#827717',
                'event', '#bf360c',
                'detail', '#7e57c2',
                '#c9a55c',
              ],
              'circle-radius': 8,
              'circle-stroke-color': '#fffdf2',
              'circle-stroke-width': 2.5,
              'circle-opacity': 0.95,
            }}
          />
          <Layer
            id="antwerp-historical-label"
            type="symbol"
            minzoom={13}
            {...(antwerpCategoryFilter ? { filter: antwerpCategoryFilter } : {})}
            layout={{
              'text-field': ['get', 'name'],
              'text-font': ['Noto Sans Regular'],
              'text-size': 11,
              'text-offset': [0, 1.3],
              'text-anchor': 'top',
              'text-allow-overlap': false,
              'text-max-width': 9,
              visibility: antwerpLayerVisible ? 'visible' : 'none',
            }}
            paint={{
              'text-color': '#1a1410',
              'text-halo-color': '#fffdf2',
              'text-halo-width': 1.5,
            }}
          />
        </Source>

        <Source id="businesses" type="geojson" data={businessCollection}>
          <Layer
            id="business-glow"
            type="circle"
            paint={{
              'circle-color': '#c9a55c',
              'circle-radius': 22,
              'circle-opacity': 0.45,
              'circle-blur': 0.6,
            }}
          />
          <Layer
            id="business-marker"
            type="circle"
            paint={{
              'circle-color': '#c9a55c',
              'circle-radius': 12,
              'circle-stroke-color': '#1a1410',
              'circle-stroke-width': 2.5,
              'circle-opacity': 1,
            }}
          />
          <Layer
            id="business-label"
            type="symbol"
            layout={{
              'text-field': ['get', 'name'],
              'text-font': ['Noto Sans Bold'],
              'text-size': 12,
              'text-offset': [0, 1.4],
              'text-anchor': 'top',
              'text-allow-overlap': true,
            }}
            paint={{
              'text-color': '#1a1410',
              'text-halo-color': '#fffdf2',
              'text-halo-width': 1.5,
            }}
          />
        </Source>

        <PaperFrameOverlay />

        <BaselinePopup />
      </MapLibreMap>
    </div>
  );
}
